import React, { useState } from 'react';
import { GamificationPermissions } from '@/constants/permissions';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Edit } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useHasPermission } from '@/hooks/use-permission';
import { walletService } from '@/services/wallet.service';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AdjustBalanceModal } from './components/adjust-balance-modal';
import { TransactionHistoryTable } from './components/transaction-history-table';

export function WalletDetailsPage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [showAdjustModal, setShowAdjustModal] = useState(false);

  // Check permissions
  const { hasPermission: canAdjustBalance } = useHasPermission(
    GamificationPermissions.Wallet.AdminAdjust,
  );

  // Fetch wallet details
  const {
    data: wallet,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['wallet-details', userId],
    queryFn: () => walletService.getUserWalletDetails(userId!),
    enabled: !!userId,
  });

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="p-6">
            <p className="text-danger">Failed to load wallet details</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLoading || !wallet) {
    return (
      <div className="container mx-auto p-6 space-y-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/4" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-32 bg-muted rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/gamification/wallets')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Wallet Details</h1>
            <p className="text-muted-foreground mt-1">User ID: {userId}</p>
          </div>
        </div>
        {canAdjustBalance && (
          <Button onClick={() => setShowAdjustModal(true)}>
            <Edit className="w-4 h-4 mr-2" />
            Adjust Balance
          </Button>
        )}
      </div>

      {/* Balance Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-5">
            <p className="text-sm text-muted-foreground mb-1">
              Current Balance
            </p>
            <p className="text-3xl font-bold text-primary">
              {wallet.currentBalance.toLocaleString()}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <p className="text-sm text-muted-foreground mb-1">Total Earned</p>
            <p className="text-3xl font-bold text-success">
              +{(wallet.lifetimeEarned || 0).toLocaleString()}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <p className="text-sm text-muted-foreground mb-1">Total Spent</p>
            <p className="text-3xl font-bold text-danger">
              -{(wallet.lifetimeSpent || 0).toLocaleString()}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Transaction History */}
      <Card>
        <CardHeader>
          <CardTitle>Transaction History</CardTitle>
        </CardHeader>
        <CardContent>
          <TransactionHistoryTable userId={userId!} />
        </CardContent>
      </Card>

      {/* Adjust Balance Modal */}
      {showAdjustModal && (
        <AdjustBalanceModal
          open={showAdjustModal}
          onClose={() => setShowAdjustModal(false)}
          userId={userId!}
          userName={`User ${userId}`}
          currentBalance={wallet.currentBalance}
        />
      )}
    </div>
  );
}

export default WalletDetailsPage;
